/*
 * <afaq-reel> — arrow controls for the vertical testimonial strip.
 *
 * A custom element rather than a script that queries the page on load: the
 * theme editor re-renders a section by assigning innerHTML, which never runs
 * injected <script> tags but *does* upgrade custom elements. So a reel added
 * or reordered in the editor wires itself with no extra plumbing.
 *
 * Direction is read from the document, not assumed. In RTL the scroller's
 * scrollLeft runs negative in every current engine, so positions are compared
 * through Math.abs and "next" means scrolling towards more-negative values.
 */
class AfaqReel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-reel-track]');
    this.prev = this.querySelector('[data-reel-prev]');
    this.next = this.querySelector('[data-reel-next]');
    if (!this.track || !this.prev || !this.next) return;

    this.rtl = getComputedStyle(this).direction === 'rtl';

    this.onScroll = this.update.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    this.prev.addEventListener('click', () => this.step(-1));
    this.next.addEventListener('click', () => this.step(1));

    this.update();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  // One card plus its gap, so a click always lands on a card edge.
  get stride() {
    const card = this.track.querySelector('li');
    if (!card) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  /*
   * RTL scrollers report scrollLeft as a negative number in every current
   * engine, so scrolling towards later cards means going *down* from 0. The
   * sign flip is the only direction-specific thing here; the browser handles
   * the easing and honours prefers-reduced-motion on its own.
   */
  step(direction) {
    const delta = this.stride * direction * (this.rtl ? -1 : 1);
    this.track.scrollBy({ left: delta, behavior: 'smooth' });
  }

  update() {
    const { scrollWidth, clientWidth, scrollLeft } = this.track;
    const overflows = scrollWidth - clientWidth > 2;

    // Nothing to page through — hide the controls rather than show dead ones.
    this.toggleAttribute('data-reel-static', !overflows);
    if (!overflows) return;

    const position = Math.abs(scrollLeft);
    const max = scrollWidth - clientWidth;
    this.prev.disabled = position <= 2;
    this.next.disabled = position >= max - 2;
  }
}

if (!customElements.get('afaq-reel')) customElements.define('afaq-reel', AfaqReel);
